import { BUY_SELL_ORDER_POLICY_DENIAL_CODES } from './codes/buySellOrder';
import { CHARGE_POLICY_DENIAL_CODES } from './codes/charge';
import { DOCUMENT_ONBOARDING_POLICY_DENIAL_CODES } from './codes/documentOnboarding';
import { GROWER_PRODUCT_POLICY_DENIAL_CODES } from './codes/growerProduct';
import { INVENTORY_TRANSFER_POLICY_DENIAL_CODES } from './codes/inventoryTransfer';
import { KERNEL_FIXTURE_POLICY_DENIAL_CODES, KERNEL_POLICY_DENIAL_CODES } from './codes/kernel';
import { LOT_POLICY_DENIAL_CODES } from './codes/lot';
import { PALLET_TAG_POLICY_DENIAL_CODES } from './codes/palletTag';
import { PRODUCTION_RUN_POLICY_DENIAL_CODES } from './codes/productionRun';
import { PURCHASE_ORDER_POLICY_DENIAL_CODES } from './codes/purchaseOrder';
import { SALES_ORDER_POLICY_DENIAL_CODES } from './codes/salesOrder';
import { SHIPMENT_POLICY_DENIAL_CODES } from './codes/shipment';

/**
 * ONE decision shape for every visibility/editability/action question, on both
 * sides of the wire.
 *
 *   allowed: true   ──▶ render it / let the mutation through
 *   allowed: false  ──▶ view:  HIDE the surface (no trace)
 *                       other: lock the surface + show `message`
 *                       AND throw AuthorizationError{code, userMessage: message}
 *
 * A denial ALWAYS carries a code and a message. There is no bare `false`
 * anywhere in this module — a lock the user cannot have explained to them is
 * a bug, and `deny()`'s signature is what prevents one being written.
 */
export type Allowed = { allowed: true };

export const POLICY_DENIAL_CODES = [
  ...KERNEL_POLICY_DENIAL_CODES,
  ...SALES_ORDER_POLICY_DENIAL_CODES,
  ...PURCHASE_ORDER_POLICY_DENIAL_CODES,
  ...GROWER_PRODUCT_POLICY_DENIAL_CODES,
  ...CHARGE_POLICY_DENIAL_CODES,
  ...BUY_SELL_ORDER_POLICY_DENIAL_CODES,
  ...PRODUCTION_RUN_POLICY_DENIAL_CODES,
  ...INVENTORY_TRANSFER_POLICY_DENIAL_CODES,
  ...LOT_POLICY_DENIAL_CODES,
  ...PALLET_TAG_POLICY_DENIAL_CODES,
  ...SHIPMENT_POLICY_DENIAL_CODES,
  ...DOCUMENT_ONBOARDING_POLICY_DENIAL_CODES,
  ...KERNEL_FIXTURE_POLICY_DENIAL_CODES,
] as const;

export type PolicyDenialCode = (typeof POLICY_DENIAL_CODES)[number];

/** A transport recognises a policy denial by matching this catalog — never by a code prefix and never by prose. `instanceof` alone is not enough either: the api recogniser pairs `instanceof AuthorizationError` WITH this catalog, so an unrelated AuthorizationError cannot inherit the policy transport. */
export const isPolicyDenialCode = (value: unknown): value is PolicyDenialCode =>
  typeof value === 'string' && POLICY_DENIAL_CODES.some((code) => code === value);

export type Denied = {
  allowed: false;
  /** Stable machine code. Surfaces match on this, never on the prose. */
  code: PolicyDenialCode;
  /** User-safe sentence. Becomes the cell tooltip AND AppError.userMessage. */
  message: string;
  /** What the user can do about it, when there is anything. */
  suggestion?: string;
};

export type Decision = Allowed | Denied;

export const allow = (): Allowed => ({ allowed: true });

export const deny = (code: PolicyDenialCode, message: string, suggestion?: string): Denied => ({
  allowed: false,
  code,
  message,
  ...(suggestion === undefined ? {} : { suggestion }),
});

/**
 * What is being attempted.
 *
 *   view     can the actor SEE it — the page, the entity, or one field/column.
 *            A view denial renders as HIDDEN, never as disabled-with-tooltip.
 *   add      create a new row/entity in this collection.
 *   edit     change it — entity-wide when no field is given (header forms,
 *            Edit buttons), field-granular when one is (grid column key IS
 *            the field).
 *   delete   remove it.
 *   perform  a named entity operation the policy declares in `ops`
 *            ('void', 'finalize', …). "Add a charge" is `add` on the
 *            charges collection; "cancel the order" is perform('cancel').
 */
export type Action<Field extends string = string, Op extends string = never> =
  | { type: 'view'; field?: Field }
  | { type: 'add' }
  | { type: 'edit'; field?: Field }
  | { type: 'delete' }
  | { type: 'perform'; op: Op };

export type ActionType = Action['type'];
